import { IRawPropertyCollectionItem, IRawPropertyMember, TRawPropertyCollectionItem } from "@propely/propely-typings";
import { getCurrentUser } from "@src/auth/getCurrentUser";
import { useRealtimeDocumentData } from "@src/data-hooks/generic/useRealtimeDocumentData";
import { queryClient } from "@src/index";
import { collection, doc, getDoc, getDocs, getFirestore, query, QuerySnapshot, where } from "firebase/firestore";
import { chunk } from "lodash";
import { useMemo } from "react";
import { useQueries, useQuery } from "react-query";
import { usePropertyMember, usePropertyMembersForUser } from "../../member/propertyMembers.service";
import { usePropertyCollectionItemsKey } from "../propertyCollection.service";
import {
  getAllPropertyCollectionItemFiles,
  getPropertyCollectionItemFiles,
  getPropertyCollectionItemFolders,
  PropertyCollectionItemEntriesInput,
} from "./propertyCollectionItem.api";

export type GetPropertyCollectionEntriesInput = PropertyCollectionItemEntriesInput;

export const usePropertyCollectionItemAllFilesKey = (input: GetPropertyCollectionEntriesInput) => [
  "property-collection-item-all-files",
  {
    collectionID: input.collectionId,
    itemID: input.itemId,
    propertyID: input.propertyId,
  },
];
export const usePropertyCollectionItemFilesInFolderKey = (input: GetPropertyCollectionEntriesInput) => [
  "property-collection-item-folder-files",
  input,
];
export const usePropertyCollectionItemFoldersKey = (input: GetPropertyCollectionEntriesInput) => ["property-collection-item-folders", input];

export const usePropertyCollectionItemAllFiles = (input: GetPropertyCollectionEntriesInput) => {
  const $files = useQuery(usePropertyCollectionItemAllFilesKey(input), () => getAllPropertyCollectionItemFiles(input), {
    enabled: Boolean(input.propertyId && input.collectionId && input.itemId),
  });

  return {
    $files,
    files: $files.data,
  };
};

export const usePropertyCollectionItemFilesInFolder = (input: GetPropertyCollectionEntriesInput) => {
  const isEnabled = Boolean(input.propertyId && input.collectionId && input.itemId);

  return useQuery(usePropertyCollectionItemFilesInFolderKey(input), async () => getPropertyCollectionItemFiles(input), {
    enabled: isEnabled,
  });
};

export const usePropertyCollectionItemFolders = (input: GetPropertyCollectionEntriesInput) => {
  const isEnabled = Boolean(input.propertyId && input.collectionId && input.itemId);
  return useQuery(usePropertyCollectionItemFoldersKey(input), async () => getPropertyCollectionItemFolders(input), {
    enabled: isEnabled,
  });
};

export const usePropertyCollectionItemByIDKey = (input: GetPropertyCollectionItemByIdInput) => [
  "properties",
  { propertyId: input.propertyId, collectionId: input.collectionId, collectionItemId: input.collectionItemId },
];

const fetchPropertyCollectionItem = async (input: GetPropertyCollectionItemByIdInput) => {
  const routine = await getDoc(
    doc(getFirestore(), "properties", input.propertyId, "collections", input.collectionId, "items", input.collectionItemId)
  );
  return TRawPropertyCollectionItem.parse({
    id: routine.id,
    ...routine.data(),
  });
};

export type GetPropertyCollectionItemByIdInput = {
  propertyId: string;
  collectionId: string;
  collectionItemId: string;
};

export const usePropertyCollectionItemByID = (input: GetPropertyCollectionItemByIdInput) => {
  const $collectionItem = useQuery(usePropertyCollectionItemByIDKey(input), () => fetchPropertyCollectionItem(input));
  return {
    $collectionItem,
    collectionItem: $collectionItem.data,
  };
};

export const usePropertyCollectionItemsByIDs = ({
  propertyID,
  collectionID,
  itemIDs,
}: {
  propertyID: string;
  collectionID: string | undefined;
  itemIDs: string[];
}) => {
  const $collectionItems = useQueries(
    itemIDs.map((itemID) => ({
      queryKey: usePropertyCollectionItemByIDKey({ propertyId: propertyID, collectionId: collectionID ?? "", collectionItemId: itemID }),
      queryFn: () => fetchPropertyCollectionItem({ propertyId: propertyID, collectionId: collectionID ?? "", collectionItemId: itemID }),
      enabled: Boolean(propertyID && collectionID && itemID),
    }))
  );
  const collectionItems: IRawPropertyCollectionItem[] = useMemo(() => {
    return $collectionItems.map((item) => item.data).filter((item) => !!item);
  }, [$collectionItems]);

  const isLoading = $collectionItems.some((query) => query.isLoading);

  return {
    $collectionItems,
    collectionItems,
    isLoading,
  };
};

export const loadPropertyCollectionItemByID = ({ propertyId, collectionId, collectionItemId }: GetPropertyCollectionItemByIdInput) => {
  return fetchPropertyCollectionItem({ propertyId, collectionId, collectionItemId });
};

export const useRealtimePropertyCollectionItem = (input: GetPropertyCollectionItemByIdInput) => {
  const $collectionItem = useRealtimeDocumentData(
    `properties/${input.propertyId}/collections/${input.collectionId}/items`,
    TRawPropertyCollectionItem,
    input.collectionItemId
  );
  return {
    $collectionItem,
    collectionItem: $collectionItem.data,
  };
};

const fetchPropertyCollectionItems = async (
  propertyID: string | undefined,
  collectionID: string | undefined,
  member: IRawPropertyMember | undefined
): Promise<IRawPropertyCollectionItem[]> => {
  // Check for member roles
  const memberRoles = member?.roles ?? [];
  if (!member || !memberRoles.length || !propertyID || !collectionID) return [];

  // Base query
  const result: IRawPropertyCollectionItem[] = [];
  const promises: Promise<void>[] = [];
  const db = getFirestore();
  const collectionRef = collection(db, "properties", propertyID, "collections", collectionID, "items");

  // Utility to parse query result
  const parseQueryResult = (querySnapshot: QuerySnapshot) => {
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const parsedData = TRawPropertyCollectionItem.safeParse({
        id: doc.id,
        ...data,
      });
      if (parsedData.success) {
        result.push(parsedData.data);
      }
    });
  };

  // Load items with member roles
  promises.push(getDocs(query(collectionRef, where("readAccess.roles", "array-contains-any", memberRoles))).then(parseQueryResult));

  if (member.contractorGroups?.length) {
    const chunked = chunk(member.contractorGroups, 10);
    chunked.forEach((groups) => {
      promises.push(getDocs(query(collectionRef, where("readAccess.contractorGroups", "array-contains-any", groups))).then(parseQueryResult));
    });
  }

  if (member.units?.length) {
    const chunked = chunk(member.units, 10);
    chunked.forEach((units) => {
      promises.push(getDocs(query(collectionRef, where("readAccess.propertyUnits", "array-contains-any", units))).then(parseQueryResult));
    });
  }

  await Promise.allSettled(promises);
  return result;
};

export const useAllPropertyCollectionItemsKey = (propertyID: string | undefined, collectionID: string | undefined) => [
  "properties",
  { propertyID, collectionID },
  "items",
];

export const useAllPropertyCollectionItems = (propertyID: string | undefined, collectionID: string | undefined) => {
  const { userId } = getCurrentUser();
  const { propertyMember } = usePropertyMember(propertyID, userId);

  const result = useQuery(
    useAllPropertyCollectionItemsKey(propertyID, collectionID),
    () => fetchPropertyCollectionItems(propertyID, collectionID, propertyMember),
    {
      enabled: !!propertyMember,
    }
  );

  return {
    $collectionItems: result,
    collectionItems: result.data,
  };
};

export const useMultiplePropertyCollectionItems = (propertyIDs: string[], collectionID: string) => {
  const { userId } = getCurrentUser();
  const { propertyMembers } = usePropertyMembersForUser(propertyIDs, userId);

  // Memoize the array of query configs so they only change if dependencies change.
  const queryConfigs = useMemo(() => {
    return propertyIDs.map((propertyID) => {
      // Find the property member for this property.
      const propertyMember = propertyMembers?.find((pm) => pm.propertyID === propertyID);
      return {
        queryKey: useAllPropertyCollectionItemsKey(propertyID, collectionID),
        queryFn: () => fetchPropertyCollectionItems(propertyID, collectionID, propertyMember),
        enabled: !!propertyMember,
      };
    });
  }, [propertyIDs, collectionID, propertyMembers]);

  // Execute queries in parallel using the memoized config.
  const queries = useQueries(queryConfigs);

  // Aggregate results into an object keyed by propertyID.
  const collectionItems = useMemo(() => {
    return propertyIDs.reduce(
      (acc, propertyID, idx) => {
        acc[propertyID] = queries[idx]?.data ?? [];
        return acc;
      },
      {} as Record<string, IRawPropertyCollectionItem[]>
    );
  }, [queries, propertyIDs]);

  // Determine if all queries have finished loading.
  const isFinishedLoading = useMemo(() => queries.every((query) => !query.isLoading), [queries]);

  return {
    $collectionItems: queries,
    collectionItems,
    isFinishedLoading,
  };
};

export const invalidatePropertyCollectionItemFiles = (input: GetPropertyCollectionEntriesInput) => {
  queryClient.invalidateQueries(usePropertyCollectionItemsKey({ collectionId: input.collectionId, propertyId: input.propertyId }));
  queryClient.invalidateQueries(
    usePropertyCollectionItemAllFilesKey({ collectionId: input.collectionId, propertyId: input.propertyId, itemId: input.itemId })
  );
  queryClient.invalidateQueries(
    usePropertyCollectionItemFilesInFolderKey({ collectionId: input.collectionId, propertyId: input.propertyId, itemId: input.itemId })
  );
};
